import React, { useContext } from 'react'
import { AppContext } from '../context/AppContext';
import MensionUsers from './MensionUsers';
import fileIcon from '../assets/images/fileIcon.svg';

const IMAGE_RE = /\.(png|jpe?g|gif|webp|bmp|svg)(\?|#|$|\/)/i;
const VIDEO_RE = /\.(mp4|webm|mov|m4v|ogv|avi|mkv)(\?|#|$|\/)/i;
const AUDIO_RE = /\.(mp3|m4a|wav|ogg|aac)(\?|#|$|\/)/i;

const getFileMeta = (url) => {
    if (!url || typeof url !== 'string') return { name: 'Document', ext: 'FILE' };
    const cleanPath = url.split('?')[0].split('#')[0];
    let name = cleanPath.split('/').pop() || 'Document';
    try {
        name = decodeURIComponent(name);
    } catch (_) {
        // leave the raw segment if decoding fails
    }
    const dotIdx = name.lastIndexOf('.');
    const ext = dotIdx >= 0 ? name.slice(dotIdx + 1).toUpperCase() : 'FILE';
    return { name, ext };
};

// `newMessages` items carry attachments as `files: string[]`; `conversations`
// items carry a single `Resource_Path: string`. Probe both before deciding the
// media kind so a reply quote keeps file context regardless of source.
const detectMedia = (repliedTo) => {
    if (!repliedTo) return null;
    const url =
        (Array.isArray(repliedTo.files) && repliedTo.files[0]) ||
        repliedTo.Resource_Path ||
        null;
    if (!url || typeof url !== 'string') return null;
    if (IMAGE_RE.test(url)) return { kind: 'image', url };
    if (VIDEO_RE.test(url)) return { kind: 'video', url };
    const meta = getFileMeta(url);
    if (AUDIO_RE.test(url)) return { kind: 'audio', url, ...meta };
    return { kind: 'file', url, ...meta };
};

// Renders the quoted-message slot above a reply bubble. It must show ONLY the
// original message being replied to — the parent (MessageList) already renders
// the new reply text inside its own bubble, so duplicating `msg` here produced
// the "same text above and below" effect, especially visible on nested replies.
export default function RepliesTo({ sernderName, msg, repliedTo, msgRef, mensioned, index }) {
    const { conversations, newMessages } = useContext(AppContext)

    // Prefer the quoted message's sender; fall back to the caller-provided
    // `sernderName` (which is the new-message sender) only when the quoted
    // sender isn't available, so the label still has something to show.
    const quotedSender =
        (repliedTo &&
            (repliedTo.sender_name ||
                [repliedTo.FirstName, repliedTo.LastName].filter(Boolean).join(" "))) ||
        sernderName ||
        ""

    const quotedText =
        (repliedTo && (repliedTo.Team_Message_Text || repliedTo.message)) || ""

    const media = detectMedia(repliedTo)

    // When the original message is a file/photo/video with no text, surface a
    // human-readable placeholder ("Photo", "Video", filename) so the quote
    // still conveys what was replied to.
    const previewText = quotedText
        ? null
        : media
            ? media.kind === 'image'
                ? 'Photo'
                : media.kind === 'video'
                    ? 'Video'
                    : media.name || (media.kind === 'audio' ? 'Audio' : 'File')
            : null

    const scrollToOriginal = () => {
        if (!repliedTo) return
        const msgIndex = conversations.indexOf(repliedTo)
        const reference = msgIndex !== -1
            ? msgRef && msgRef.current && msgRef.current[msgIndex]
            : msgRef && msgRef.current && msgRef.current[conversations.length + newMessages.indexOf(repliedTo)]
        reference && reference.scrollIntoView && reference.scrollIntoView()
    }

    const thumbWrapStyle = {
        flex: '0 0 auto',
        width: 36,
        height: 36,
        borderRadius: 4,
        overflow: 'hidden',
        background: 'rgba(0,0,0,0.06)',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
    };
    const thumbMediaStyle = { width: '100%', height: '100%', objectFit: 'cover' };
    const thumbIconStyle = { width: 18, height: 18, opacity: 0.8 };

    const renderThumb = () => {
        if (!media) return null;
        if (media.kind === 'image') {
            return (
                <span style={thumbWrapStyle}>
                    <img src={media.url} alt="" style={thumbMediaStyle} />
                </span>
            );
        }
        if (media.kind === 'video') {
            return (
                <span style={thumbWrapStyle}>
                    <video src={media.url} muted preload="metadata" playsInline style={thumbMediaStyle} />
                </span>
            );
        }
        return (
            <span style={thumbWrapStyle}>
                <img src={fileIcon} alt="" style={thumbIconStyle} />
            </span>
        );
    };

    return (
        <div className={"repliesTo "} style={{ maxWidth: '70%' }}>
            <div className='receive-msg' >
                <div className="right-receive-msg" onClick={scrollToOriginal} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                    {renderThumb()}
                    <div style={{ minWidth: 0, flex: 1 }}>
                        <span className="senderName">{quotedSender}</span>
                        {quotedText ?
                            <div className="receive-msg-main">
                                <p className="msg-text" style={{ textAlign: 'start' }}>{
                                    quotedText.includes("@") ?
                                        <MensionUsers text={quotedText} users={mensioned} index={index} />
                                        :
                                        quotedText}</p>
                            </div>
                            : previewText ?
                                <div className="receive-msg-main">
                                    <p className="msg-text" style={{ textAlign: 'start', fontStyle: 'italic', opacity: 0.85 }}>
                                        {previewText}
                                    </p>
                                </div>
                                : null
                        }
                    </div>
                </div>
            </div>
        </div>
    )
}
